import { useEffect, useRef, useState } from 'react'
import { LoadingState, toastError } from 'admination-react'
import { getAmpelSchwellen, updateAmpelSchwellen } from '@/core/data/organisationAmpel'
import { isValidSchwellen, type AmpelSchwellen } from '@/core/lib/ampel'
import { TextField } from '@/core/components/form/TextField'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'

type AmpelForm = { gelbTage: string; rotTage: string }

const toForm = (s: AmpelSchwellen): AmpelForm => ({ gelbTage: String(s.gelbTage), rotTage: String(s.rotTage) })

export function schwellenFehler(form: AmpelForm): string | null {
  if (form.gelbTage === '' || form.rotTage === '') return 'Bitte beide Werte in Tagen angeben.'
  const s = { gelbTage: Number(form.gelbTage), rotTage: Number(form.rotTage) }
  if (s.gelbTage < 1) return 'Gelb frühestens nach 1 Tag.'
  if (!isValidSchwellen(s)) return 'Rot muss später als Gelb kommen.'
  return null
}

export function AmpelSettingsSection({ organisationId }: { organisationId: string }) {
  const [form, setForm] = useState<AmpelForm | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const formRef = useRef<AmpelForm | null>(null)
  formRef.current = form
  const lastSaved = useRef<AmpelForm | null>(null)

  useEffect(() => {
    let cancelled = false
    getAmpelSchwellen(organisationId)
      .then((s) => {
        if (cancelled) return
        setForm(toForm(s))
        lastSaved.current = toForm(s)
      })
      .catch((e) => {
        console.error('Ampel-Einstellungen laden fehlgeschlagen:', e)
        if (!cancelled) toastError('Ampel-Einstellungen konnten nicht geladen werden.')
      })
    return () => {
      cancelled = true
    }
  }, [organisationId])

  const set = (key: keyof AmpelForm) => (v: string) => {
    setFehler(null)
    setForm((f) => (f ? { ...f, [key]: v } : f))
  }

  function commit() {
    const cur = formRef.current
    const saved = lastSaved.current
    if (!cur || !saved) return
    if (cur.gelbTage === saved.gelbTage && cur.rotTage === saved.rotTage) return
    const reason = schwellenFehler(cur)
    setFehler(reason)
    if (reason) return
    const next = { gelbTage: Number(cur.gelbTage), rotTage: Number(cur.rotTage) }
    const identity = captureWriteIdentity()
    lastSaved.current = toForm(next)
    void updateAmpelSchwellen(organisationId, next).catch((e) => {
      if (!isWriteIdentityCurrent(identity)) return
      lastSaved.current = saved
      console.error('Ampel-Schwellen speichern fehlgeschlagen:', e)
      toastError(friendlyErrorMessage(e, 'Speichern fehlgeschlagen.'))
    })
  }

  const field = (key: keyof AmpelForm, label: string, hint: string) => (
    <div
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLElement).blur()
      }}
    >
      <TextField
        label={label}
        value={form?.[key] ?? ''}
        onChange={set(key)}
        hint={{ tone: 'info', text: hint }}
        filter={/[0-9]/}
        inputMode="numeric"
        maxLength={3}
        invalid={fehler !== null}

        errorMessage={key === 'rotTage' ? (fehler ?? undefined) : undefined}
      />
    </div>
  )

  return (
    <div className="section">
      <h3 className="section-header">Ampel</h3>
      <div className="section-body">
        {form === null ? (
          <LoadingState>Die Ampel-Einstellungen werden geladen …</LoadingState>
        ) : (
          <>
            {field('gelbTage', 'Gelb nach Tagen', 'Eine Karte, die so viele Tage in ihrer Spalte wartet, wird auf dem Board gelb.')}
            {field('rotTage', 'Rot nach Tagen', 'Ab so vielen Tagen wird sie rot.')}
          </>
        )}
      </div>
    </div>
  )
}
